import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PaginationControls } from "@/components/common/PaginationControl";
import { CouponTableSkeleton } from "@/components/ui/coupon-table-skeleton";
import { Plus, Eye, Edit, Trash2 } from "lucide-react";
import {
  CouponApi,
  CouponRuleConfig,
  GetAllCouponRuleConfigRequest,
  DeleteCouponRuleRequest,
} from "@/api/giftcard.api";
import { useToast } from "@/hooks/use-toast";
import { HandleError } from "@/api/axiosClient";
import { CreateCouponForm } from "./CreateCouponForm";
import { EditCouponForm } from "./EditCouponForm";
import {
  getCouponTypeLabel,
  getRuleTypeLabel,
  formatInvoiceValue,
  formatDate,
} from "./utils";

export function CouponsRules() {
  const [coupons, setCoupons] = useState<CouponRuleConfig[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [couponPage, setCouponPage] = useState<number>(0);
  const [couponPageSize, setCouponPageSize] = useState<number>(10);
  const [couponTotalRecords, setCouponTotalRecords] = useState<number>(0);
  const [selectedCoupon, setSelectedCoupon] = useState<CouponRuleConfig | null>(
    null
  );
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [couponToDelete, setCouponToDelete] = useState<CouponRuleConfig | null>(
    null
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const { toast } = useToast();

  const fetchCoupons = async () => {
    setIsLoading(true);
    try {
      const payload: GetAllCouponRuleConfigRequest = {
        p_PageNumber: couponPage + 1,
        p_PageSize: couponPageSize,
      };
      const response = await CouponApi.getAllCouponRuleConfig(payload);
      if (response.couponRuleList && Array.isArray(response.couponRuleList)) {
        setCoupons(response.couponRuleList);
      } else {
        setCoupons([]);
      }
      if (response.total_records !== undefined) {
        setCouponTotalRecords(response.total_records);
      }
      if (response.error_msg && response.error_msg.trim() !== "") {
        toast({
          title: "Warning",
          description: response.error_msg,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error fetching coupons:", error);
      const errorMessage = HandleError(error);
      toast({
        title: "Error",
        description: errorMessage || "Failed to fetch coupons",
        variant: "destructive",
      });
      setCoupons([]);
      setCouponTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couponPage, couponPageSize]);

  const handleDeleteClick = (coupon: CouponRuleConfig) => {
    setCouponToDelete(coupon);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!couponToDelete) return;

    setIsDeleting(true);
    try {
      const payload: DeleteCouponRuleRequest = {
        p_id: couponToDelete.id,
        p_isDelete: true,
      };

      const response = await CouponApi.deleteCouponRule(payload);

      if (response.error_msg && response.error_msg.trim() !== "") {
        toast({
          title: "Error",
          description: response.error_msg,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: response.message || "Coupon rule deleted successfully",
        });
        setIsDeleteModalOpen(false);
        setCouponToDelete(null);
        fetchCoupons();
      }
    } catch (error) {
      console.error("Error deleting coupon rule:", error);
      const errorMessage = HandleError(error);
      toast({
        title: "Error",
        description: errorMessage || "Failed to delete coupon rule",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {isLoading ? (
        <CouponTableSkeleton rowCount={5} />
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-800">Coupon Rules</h2>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setIsCreateModalOpen(true)}
                className="flex items-center gap-2 bg-green-600 text-white hover:bg-green-700"
              >
                <Plus className="h-4 w-4" />
                Create Rule
              </Button>
              <Button
                onClick={() => setIsBulkUploadModalOpen(true)}
                variant="outline"
                className="flex items-center gap-2"
              >
                Bulk Upload
              </Button>
            </div>
          </div>
          <DataTable
            title=""
            data={coupons}
            cellClassName="py-6 text-gray-700"
            columns={[
              {
                header: "Rule Name",
                accessorKey: "ruleName" as keyof CouponRuleConfig,
              },
              {
                header: "Invoice Value",
                id: "invoiceValue",
                cell: (row: CouponRuleConfig) =>
                  formatInvoiceValue(row.invoiceValueFrom, row.invoiceValueTo),
              },
              {
                header: "Coupon Type",
                id: "couponType",
                cell: (row: CouponRuleConfig) =>
                  getCouponTypeLabel(row.couponType),
              },
              {
                header: "Coupon Value",
                accessorKey: "couponValue" as keyof CouponRuleConfig,
                cell: (row: CouponRuleConfig) => row.couponValue.toString(),
              },
              {
                header: "Issuance From",
                id: "issuanceFrom",
                cell: (row: CouponRuleConfig) => formatDate(row.issuanceFrom),
              },
              {
                header: "Issuance To",
                id: "issuanceTo",
                cell: (row: CouponRuleConfig) => formatDate(row.issuanceTo),
              },
              {
                header: "Expiry",
                id: "expiry",
                cell: (row: CouponRuleConfig) => formatDate(row.expiry),
              },
              {
                header: "Rule Type",
                id: "ruleType",
                cell: (row: CouponRuleConfig) => getRuleTypeLabel(row.ruleType),
              },
              {
                header: "Action",
                id: "action",
                cell: (row: CouponRuleConfig) => (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedCoupon(row);
                        setIsViewModalOpen(true);
                      }}
                      title="View"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteClick(row)}
                      disabled={row.isUsed === true}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title={
                        row.isUsed === true
                          ? "Cannot delete: Rule is in use"
                          : "Delete"
                      }
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ),
              },
            ]}
          />

          {/* Pagination */}
          {!isLoading && couponTotalRecords > 0 && (
            <div className="mt-4 flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">Rows per page:</span>
                <Select
                  value={couponPageSize.toString()}
                  onValueChange={(value) => {
                    const newPageSize = parseInt(value, 10);
                    setCouponPageSize(newPageSize);
                    setCouponPage(0);
                  }}
                >
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5</SelectItem>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="30">30</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {Math.ceil(couponTotalRecords / couponPageSize) > 1 && (
                <PaginationControls
                  currentPage={couponPage}
                  totalPages={Math.ceil(couponTotalRecords / couponPageSize)}
                  onPrevHandler={() => setCouponPage((p) => Math.max(0, p - 1))}
                  onNextHandler={() =>
                    setCouponPage((p) =>
                      Math.min(
                        Math.ceil(couponTotalRecords / couponPageSize) - 1,
                        p + 1
                      )
                    )
                  }
                  onPageChange={(page) => setCouponPage(page)}
                />
              )}
            </div>
          )}
        </div>
      )}

      {/* Create Coupon Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Rule</DialogTitle>
          </DialogHeader>
          <CreateCouponForm
            onSuccess={() => {
              setIsCreateModalOpen(false);
              fetchCoupons();
            }}
            onCancel={() => {
              setIsCreateModalOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Modal */}
      <Dialog
        open={isBulkUploadModalOpen}
        onOpenChange={setIsBulkUploadModalOpen}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Bulk Upload</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div
              onClick={() => {
                const input = document.createElement("input");
                input.type = "file";
                input.accept = ".xlsx,.xls,.csv";
                input.onchange = (e) => {
                  const file = (e.target as HTMLInputElement).files?.[0];
                  if (file) {
                    setSelectedFile(file);
                  }
                };
                input.click();
              }}
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-gray-400 transition-colors"
            >
              {selectedFile ? (
                <div className="space-y-2">
                  <p className="text-sm text-gray-600">Selected File:</p>
                  <p className="text-sm font-medium text-gray-900">
                    {selectedFile.name}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-gray-600">
                    Click to select a file
                  </p>
                  <p className="text-xs text-gray-400">
                    Supported formats: .xlsx, .xls, .csv
                  </p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsBulkUploadModalOpen(false);
                  setSelectedFile(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (selectedFile) {
                    toast({
                      title: "Success",
                      description: "File uploaded successfully",
                    });
                    setIsBulkUploadModalOpen(false);
                    setSelectedFile(null);
                  } else {
                    toast({
                      title: "Error",
                      description: "Please select a file first",
                      variant: "destructive",
                    });
                  }
                }}
              >
                Submit
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Coupon Details Modal */}
      <Dialog
        open={isViewModalOpen}
        onOpenChange={(open) => {
          setIsViewModalOpen(open);
          if (!open) {
            setIsEditMode(false);
          }
        }}
      >
        <DialogContent
          className={
            isEditMode
              ? "sm:max-w-5xl max-h-[90vh] overflow-y-auto"
              : "sm:max-w-2xl max-h-[90vh] overflow-y-auto"
          }
        >
          <DialogHeader>
            <div className="flex items-center justify-between pr-8">
              <DialogTitle>
                {isEditMode ? "Edit Coupon Rule" : "Coupon Details"}
              </DialogTitle>
              {!isEditMode && selectedCoupon && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsEditMode(true)}
                    className="flex items-center gap-2"
                  >
                    <Edit className="h-4 w-4" />
                    Edit
                  </Button>
                </div>
              )}
            </div>
          </DialogHeader>

          {selectedCoupon && !isEditMode && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4 max-w-3xl mx-auto">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Rule Name
                  </label>
                  <p className="text-sm font-semibold mt-1">
                    {selectedCoupon.ruleName}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Coupon Type
                  </label>
                  <p className="text-sm font-semibold mt-1">
                    {getCouponTypeLabel(selectedCoupon.couponType)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Invoice Value From
                  </label>
                  <p className="text-sm font-semibold mt-1">
                    {selectedCoupon.invoiceValueFrom}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Invoice Value To
                  </label>
                  <p className="text-sm font-semibold mt-1">
                    {selectedCoupon.invoiceValueTo}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Coupon Value
                  </label>
                  <p className="text-sm font-semibold mt-1">
                    {selectedCoupon.couponValue}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Rule Type
                  </label>
                  <p className="text-sm font-semibold mt-1">
                    {getRuleTypeLabel(selectedCoupon.ruleType)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Issuance From
                  </label>
                  <p className="text-sm font-semibold mt-1">
                    {formatDate(selectedCoupon.issuanceFrom)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Issuance To
                  </label>
                  <p className="text-sm font-semibold mt-1">
                    {formatDate(selectedCoupon.issuanceTo)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Expiry Date
                  </label>
                  <p className="text-sm font-semibold mt-1">
                    {formatDate(selectedCoupon.expiry)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Redeem Amount From
                  </label>
                  <p className="text-sm font-semibold mt-1">
                    {selectedCoupon.redeemAmountFrom ||
                      selectedCoupon.RedeemAmountFrom ||
                      "-"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Redeem Amount To
                  </label>
                  <p className="text-sm font-semibold mt-1">
                    {selectedCoupon.redeemAmountTo ||
                      selectedCoupon.RedeemAmountTo ||
                      "-"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    ID
                  </label>
                  <p className="text-sm font-semibold mt-1">
                    {selectedCoupon.id}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Created Date
                  </label>
                  <p className="text-sm font-semibold mt-1">
                    {formatDate(selectedCoupon.createdDate)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Updated Date
                  </label>
                  <p className="text-sm font-semibold mt-1">
                    {formatDate(selectedCoupon.updatedDate)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {selectedCoupon && isEditMode && (
            <EditCouponForm
              coupon={selectedCoupon}
              onSuccess={() => {
                setIsEditMode(false);
                setIsViewModalOpen(false);
                fetchCoupons();
              }}
              onCancel={() => setIsEditMode(false)}
            />
          )}

          {!isEditMode && (
            <DialogFooter>
              <Button onClick={() => setIsViewModalOpen(false)}>Close</Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Coupon Rule</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600">
              Are you sure you want to delete the coupon rule{" "}
              <span className="font-semibold">{couponToDelete?.ruleName}</span>?
              This action cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setCouponToDelete(null);
              }}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
